import { FormattedMessage } from 'react-intl'
import { Link, useRouterState } from '@tanstack/react-router'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { ChartBarIcon } from '@gto/icons-react/outline/chart-bar'
import { ChartPieIcon } from '@gto/icons-react/outline/chart-pie'
import { ClipboardDocumentListIcon } from '@gto/icons-react/outline/clipboard-document-list'
import { CogIcon } from '@gto/icons-react/outline/cog'
import { CreditCardIcon } from '@gto/icons-react/outline/credit-card'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { DocumentChartBarIcon } from '@gto/icons-react/outline/document-chart-bar'
import { FolderIcon } from '@gto/icons-react/outline/folder'
import { QueueListIcon } from '@gto/icons-react/outline/queue-list'
import { ScaleIcon } from '@gto/icons-react/outline/scale'
import { ShieldCheckIcon } from '@gto/icons-react/outline/shield-check'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { TicketIcon } from '@gto/icons-react/outline/ticket'
import { UserGroupIcon } from '@gto/icons-react/outline/user-group'
import { cn } from '@gto/ui/cn'
import { Tooltip } from '@gto/ui/tooltip'
import { Typography } from '@gto/ui/typography'

import { useIsAdmin } from '@/hooks/use-is-admin'

export const ICON_SIZE = 24

type NavItemDef = {
  to: string
  labelId: string
  icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactNode
}

const PARTNER_NAV_ITEMS: NavItemDef[] = [
  { to: '/dashboard', labelId: 'nav.dashboard', icon: ChartBarIcon },
  { to: '/commissions', labelId: 'nav.commissions', icon: QueueListIcon },
  { to: '/reports', labelId: 'nav.reports', icon: ChartPieIcon },
  { to: '/coupons', labelId: 'nav.coupons', icon: TicketIcon },
  { to: '/payouts', labelId: 'nav.payouts', icon: CreditCardIcon },
  { to: '/assets', labelId: 'nav.assets', icon: FolderIcon },
  { to: '/settings', labelId: 'nav.settings', icon: CogIcon },
]

const ADMIN_NAV_ITEMS: NavItemDef[] = [
  { to: '/admin/dashboard', labelId: 'nav.admin.dashboard', icon: ChartBarIcon },
  { to: '/admin/partners', labelId: 'nav.admin.partners', icon: UserGroupIcon },
  { to: '/admin/orders', labelId: 'nav.admin.orders', icon: ShoppingCartIcon },
  { to: '/admin/reports', labelId: 'nav.admin.reports', icon: DocumentChartBarIcon },
  { to: '/admin/payouts', labelId: 'nav.admin.payouts', icon: BanknotesIcon },
  { to: '/admin/ledger', labelId: 'nav.admin.ledger', icon: ClipboardDocumentListIcon },
  { to: '/admin/settlements', labelId: 'nav.admin.settlements', icon: CurrencyDollarIcon },
  { to: '/admin/tiers', labelId: 'nav.admin.tiers', icon: ScaleIcon },
  { to: '/admin/assets', labelId: 'nav.admin.assets', icon: FolderIcon },
  { to: '/admin/access', labelId: 'nav.admin.access', icon: ShieldCheckIcon },
]

export { ADMIN_NAV_ITEMS, PARTNER_NAV_ITEMS }

type SidebarNavItemProps = {
  to: string
  labelId: string
  icon: (props: React.SVGProps<SVGSVGElement>) => React.ReactNode
  isActive: boolean
}

const SidebarNavItem = ({ to, labelId, icon: Icon, isActive }: SidebarNavItemProps) => (
  <Tooltip
    content={<FormattedMessage id={labelId} />}
    placement="right"
  >
    <Link
      style={{ textDecoration: 'none' }}
      to={to}
    >
      <div className="flex cursor-pointer flex-col items-center gap-1 [&:hover_.sidebar-icon]:text-text-primary [&:hover_.sidebar-label]:text-text-primary">
        <div
          className={cn(
            'sidebar-icon flex size-10 items-center justify-center transition-colors duration-150',
            isActive ? 'text-primary-main' : 'text-text-secondary',
          )}
        >
          <Icon
            height={ICON_SIZE}
            width={ICON_SIZE}
          />
        </div>
        <Typography
          variant="caption"
          className={cn(
            'sidebar-label block w-[68px] truncate text-center text-[0.6rem] font-medium leading-[1.2] transition-colors duration-150',
            isActive ? 'text-primary-main' : 'text-text-secondary',
          )}
        >
          <FormattedMessage id={labelId} />
        </Typography>
      </div>
    </Link>
  </Tooltip>
)

export const NavList = ({ onNavigate }: { onNavigate?: () => void }) => {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const isAdminUser = useIsAdmin()

  const navItems = isAdminUser ? ADMIN_NAV_ITEMS : PARTNER_NAV_ITEMS

  return (
    <div className="flex flex-col items-center gap-[18px]">
      {navItems.map(({ to, labelId, icon }) => (
        <div
          key={to}
          onClick={onNavigate}
        >
          <SidebarNavItem
            icon={icon}
            isActive={isAdminUser ? currentPath.startsWith(to) : currentPath === to}
            labelId={labelId}
            to={to}
          />
        </div>
      ))}
    </div>
  )
}
